// Google Drive Admin Component for EasyShopi
// Provides Google Drive integration for image management

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import googleDriveService from '../services/GoogleDriveService';
import { motion, AnimatePresence } from 'framer-motion';

const GoogleDriveAdmin = ({ onClose }) => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState('');

  // Images state
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, easyshopi, other
  const [sortBy, setSortBy] = useState('modifiedTime'); // name, createdTime, modifiedTime, size
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [nextPageToken, setNextPageToken] = useState(null);

  // Upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadMetadata, setUploadMetadata] = useState({
    title: '',
    category: '',
    productId: ''
  });

  // Error handling
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Initialize Google Drive service
  useEffect(() => {
    const initializeDrive = async () => {
      try {
        await googleDriveService.init();
        googleDriveService.restoreTokens();
        
        if (googleDriveService.isAuth()) {
          setIsAuthenticated(true);
          loadImages();
        }
        
        // Check if in demo mode
        if (googleDriveService.isDemoMode) {
          setError('Demo mode: Google Drive credentials not configured. Using placeholder images.');
        }
      } catch (error) {
        console.error('Failed to initialize Google Drive:', error);
        
        if (error.message.includes('credentials not configured')) {
          setError('Google Drive integration not configured. Please run: node setup-google-drive.js');
        } else {
          setError('Failed to initialize Google Drive service: ' + error.message);
        }
      }
    };

    initializeDrive();
  }, []);

  // Load images from Google Drive
  const loadImages = useCallback(async (pageToken = null, append = false) => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError('');

    try {
      const result = await googleDriveService.getImages(pageToken, 20);
      
      let newImages = result.images;
      
      // Apply filters
      if (filterType === 'easyshopi') {
        newImages = newImages.filter(img => img.isEasyshopiImage);
      } else if (filterType === 'other') {
        newImages = newImages.filter(img => !img.isEasyshopiImage);
      }

      // Apply search
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        newImages = newImages.filter(img => 
          img.name.toLowerCase().includes(searchLower) ||
          (img.properties.easyshopi_title && img.properties.easyshopi_title.toLowerCase().includes(searchLower)) ||
          (img.properties.easyshopi_category && img.properties.easyshopi_category.toLowerCase().includes(searchLower))
        );
      }

      // Apply sorting
      newImages.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];
        
        if (sortBy === 'size') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        } else if (sortBy === 'modifiedTime' || sortBy === 'createdTime') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });

      if (append) {
        setImages(prev => [...prev, ...newImages]);
      } else {
        setImages(newImages);
      }

      setNextPageToken(result.nextPageToken);
      setHasMore(!!result.nextPageToken);
      setCurrentPage(prev => append ? prev : 1);
    } catch (error) {
      console.error('Error loading images:', error);
      setError('Failed to load images: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, filterType, searchTerm, sortBy, sortOrder]);

  // Authenticate with Google Drive
  const handleAuthenticate = async () => {
    setIsAuthenticating(true);
    setAuthError('');

    try {
      await googleDriveService.authenticate();
      setIsAuthenticated(true);
      setSuccess('Successfully authenticated with Google Drive!');
      loadImages();
    } catch (error) {
      console.error('Authentication failed:', error);
      setAuthError('Authentication failed: ' + error.message);
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Sign out from Google Drive
  const handleSignOut = async () => {
    try {
      await googleDriveService.signOut();
      setIsAuthenticated(false);
      setImages([]);
      setSuccess('Signed out successfully');
    } catch (error) {
      console.error('Sign out failed:', error);
      setError('Failed to sign out: ' + error.message);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length !== files.length) {
      setError('Some files were rejected. Only image files are allowed.');
    }

    setSelectedFiles(validFiles);
  };

  // Upload files to Google Drive
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        try {
          const result = await googleDriveService.uploadImage(file, uploadMetadata);
          
          // Update progress
          setUploadProgress(((index + 1) / selectedFiles.length) * 100);
          
          return result;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        }
      });

      const uploadedImages = await Promise.all(uploadPromises);
      
      // Save to Firestore for product association
      for (const image of uploadedImages) {
        if (uploadMetadata.productId) {
          // Associate with existing product
          await updateDoc(doc(db, "products", uploadMetadata.productId), {
            imageURL: image.url,
            imageURLs: [image.url],
            updatedAt: new Date()
          });
        }
      }

      setSuccess(`Successfully uploaded ${uploadedImages.length} image(s)!`);
      setSelectedFiles([]);
      setUploadMetadata({ title: '', category: '', productId: '' });
      
      // Refresh images list
      loadImages();
    } catch (error) {
      console.error('Upload failed:', error);
      setError('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId, imageName) => {
    if (!window.confirm(`Are you sure you want to delete "${imageName}"?`)) {
      return;
    }

    try {
      await googleDriveService.deleteImage(imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
      setSuccess('Image deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      setError('Failed to delete image: ' + error.message);
    }
  };

  // Update image metadata
  const handleUpdateMetadata = async (imageId, newMetadata) => {
    try {
      await googleDriveService.updateImageMetadata(imageId, newMetadata);
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, properties: { ...img.properties, ...newMetadata } } : img
      ));
      setSuccess('Metadata updated successfully');
    } catch (error) {
      console.error('Metadata update failed:', error);
      setError('Failed to update metadata: ' + error.message);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Google Drive Integration</h2>
            <p className="text-gray-600 mb-6">
              Connect your Google Drive to manage images for your EasyShopi products.
            </p>
            
            {authError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {authError}
              </div>
            )}
            
            <button
              onClick={handleAuthenticate}
              disabled={isAuthenticating}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAuthenticating ? 'Connecting...' : 'Connect Google Drive'}
            </button>
            
            <button
              onClick={onClose}
              className="w-full mt-3 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full max-h-[90vh] mx-4 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Google Drive Image Manager</h2>
            <p className="text-gray-600">Manage images stored in your Google Drive</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => loadImages()}
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleSignOut}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
            >
              Sign Out
            </button>
            <button
              onClick={onClose}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>

        {/* Upload Section */}
        <div className="p-6 border-b bg-gray-50">
          <h3 className="text-lg font-semibold mb-4">Upload New Images</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={uploadMetadata.title}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, title: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Product title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <input
                type="text"
                value={uploadMetadata.category}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, category: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Product category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product ID (optional)</label>
              <input
                type="text"
                value={uploadMetadata.productId}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, productId: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Associate with product"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Files</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {selectedFiles.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Selected files: {selectedFiles.length}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                    {file.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {uploading && (
            <div className="mb-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-blue-700">Uploading...</span>
                  <span className="text-sm text-blue-700">{Math.round(uploadProgress)}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Images'}
          </button>
        </div>

        {/* Filters and Search */}
        <div className="p-6 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Search images..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Images</option>
                <option value="easyshopi">EasyShopi Images</option>
                <option value="other">Other Images</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="modifiedTime">Modified Date</option>
                <option value="createdTime">Created Date</option>
                <option value="name">Name</option>
                <option value="size">File Size</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 m-6 rounded"
            >
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 m-6 rounded"
            >
              {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Images Grid */}
        <div className="flex-1 overflow-auto p-6">
          {loading && images.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading images...</p>
              </div>
            </div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-gray-400 text-6xl mb-4">📁</div>
                <p className="text-gray-600">No images found</p>
                <p className="text-gray-500 text-sm">Upload some images to get started</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <AnimatePresence>
                {images.map((image) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <div className="aspect-square relative">
                      <img
                        src={image.thumbnailUrl || image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = image.url;
                        }}
                      />
                      {image.isEasyshopiImage && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                          EasyShopi
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3">
                      <h4 className="font-medium text-sm truncate mb-1" title={image.name}>
                        {image.name}
                      </h4>
                      <p className="text-xs text-gray-500 mb-2">
                        {formatFileSize(image.size)} • {formatDate(image.modifiedTime)}
                      </p>
                      
                      {image.properties.easyshopi_title && (
                        <p className="text-xs text-blue-600 mb-1">
                          Title: {image.properties.easyshopi_title}
                        </p>
                      )}
                      
                      {image.properties.easyshopi_category && (
                        <p className="text-xs text-purple-600 mb-2">
                          Category: {image.properties.easyshopi_category}
                        </p>
                      )}
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDeleteImage(image.id, image.name)}
                          className="flex-1 bg-red-600 text-white text-xs py-1 px-2 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                        <a
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-blue-600 text-white text-xs py-1 px-2 rounded hover:bg-blue-700 text-center"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {hasMore && !loading && (
            <div className="text-center mt-6">
              <button
                onClick={() => loadImages(nextPageToken, true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleDriveAdmin;